import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { collectDiagnostics } from '../utils/diagnosticsCollector';
import { diagnosticsApi } from '../services/api';
import DiagnosticAttachments, { DiagnosticAttachmentsValue } from '../components/DiagnosticAttachments';
import { buildGeoErrorMessage, detectGeoPlatform, isOsLocationServicesOff } from '../utils/geoErrors';

// Leaflet's default marker icons rely on relative paths that break inside
// Vite's bundling. Set explicit URLs to the icons inside the leaflet package
// so they resolve via Vite's asset pipeline.
// @ts-expect-error - augmenting Leaflet's internal Icon.Default prototype
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
});

interface FixSample {
  receivedAt: number;
  source: 'watch' | 'one-shot';
  latitude: number;
  longitude: number;
  accuracy: number;
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  speed: number | null;
  timestamp: number;
}

interface ErrorSample {
  receivedAt: number;
  source: 'watch' | 'one-shot';
  code: number;
  message: string;
}

function geoErrorName(code: number): string {
  switch (code) {
    case 1:
      return 'PERMISSION_DENIED';
    case 2:
      return 'POSITION_UNAVAILABLE';
    case 3:
      return 'TIMEOUT';
    default:
      return `UNKNOWN_${code}`;
  }
}

function formatAge(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = s / 60;
  return `${m.toFixed(1)}m`;
}

function MapRecenter({ lat, lon }: { lat: number; lon: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lon], map.getZoom() || 17);
  }, [lat, lon, map]);
  return null;
}

export default function GpsDiagnostics() {
  const navigate = useNavigate();
  const [permission, setPermission] = useState<PermissionState | 'unknown'>('unknown');
  const [watchSupported, setWatchSupported] = useState(true);
  const [watchFix, setWatchFix] = useState<FixSample | null>(null);
  const [oneShotFix, setOneShotFix] = useState<FixSample | null>(null);
  const [watchError, setWatchError] = useState<ErrorSample | null>(null);
  const [oneShotError, setOneShotError] = useState<ErrorSample | null>(null);
  const [watchCount, setWatchCount] = useState(0);
  const [oneShotRunning, setOneShotRunning] = useState(false);
  const [accuracyMin, setAccuracyMin] = useState<number | null>(null);
  const [accuracyMax, setAccuracyMax] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const watchIdRef = useRef<number | null>(null);

  // Submit-to-support state (#1873)
  const [repNote, setRepNote] = useState('');
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitRefId, setSubmitRefId] = useState<number | null>(null);
  // Attachments (notes + screenshots) #1965. Note: this is distinct
  // from the legacy repNote field above — repNote is GPS-specific
  // ("what does the rep think about this GPS fix?"), attachments.notes
  // is the general what-happened narrative shared across all diag types.
  const [attachments, setAttachments] = useState<DiagnosticAttachmentsValue>({
    notes: '',
    screenshots: [],
  });

  // Permission state probe
  useEffect(() => {
    if (!('permissions' in navigator)) return;
    (navigator.permissions as Permissions)
      .query({ name: 'geolocation' as PermissionName })
      .then((status) => {
        setPermission(status.state);
        status.onchange = () => setPermission(status.state);
      })
      .catch(() => setPermission('unknown'));
  }, []);

  // Live "now" ticker so age-of-fix updates in the UI
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  // Start watchPosition with the strictest settings the browser allows
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setWatchSupported(false);
      return;
    }
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const sample: FixSample = {
          receivedAt: Date.now(),
          source: 'watch',
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          altitude: pos.coords.altitude,
          altitudeAccuracy: pos.coords.altitudeAccuracy,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
          timestamp: pos.timestamp,
        };
        setWatchFix(sample);
        setWatchError(null);
        setWatchCount((c) => c + 1);
        setAccuracyMin((m) => (m === null ? sample.accuracy : Math.min(m, sample.accuracy)));
        setAccuracyMax((m) => (m === null ? sample.accuracy : Math.max(m, sample.accuracy)));
      },
      (err) => {
        setWatchError({
          receivedAt: Date.now(),
          source: 'watch',
          code: err.code,
          message: err.message,
        });
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 30000,
      }
    );
    watchIdRef.current = id;
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  const runOneShot = () => {
    if (!('geolocation' in navigator)) return;
    setOneShotRunning(true);
    setOneShotError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOneShotFix({
          receivedAt: Date.now(),
          source: 'one-shot',
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          altitude: pos.coords.altitude,
          altitudeAccuracy: pos.coords.altitudeAccuracy,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
          timestamp: pos.timestamp,
        });
        setOneShotRunning(false);
      },
      (err) => {
        setOneShotError({
          receivedAt: Date.now(),
          source: 'one-shot',
          code: err.code,
          message: err.message,
        });
        setOneShotRunning(false);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 30000,
      }
    );
  };

  const buildClipboardText = (): string => {
    const lines: string[] = [];
    lines.push(`GPS Diagnostics - ${new Date().toISOString()}`);
    lines.push(`UA: ${navigator.userAgent}`);
    lines.push(`Permission: ${permission}`);
    lines.push(`Watch supported: ${watchSupported}`);
    lines.push('');
    lines.push('-- watchPosition (live) --');
    if (watchFix) {
      lines.push(`fix#${watchCount} lat=${watchFix.latitude} lon=${watchFix.longitude}`);
      lines.push(`accuracy=${watchFix.accuracy}m alt=${watchFix.altitude ?? 'null'}m`);
      lines.push(`alt_acc=${watchFix.altitudeAccuracy ?? 'null'}m heading=${watchFix.heading ?? 'null'}`);
      lines.push(`speed=${watchFix.speed ?? 'null'}m/s`);
      lines.push(`fix_ts=${new Date(watchFix.timestamp).toISOString()}`);
      lines.push(`fix_age=${formatAge(now - watchFix.timestamp)}`);
      lines.push(`acc_range=[${accuracyMin?.toFixed(1) ?? '?'}, ${accuracyMax?.toFixed(1) ?? '?'}]m`);
    } else {
      lines.push('(no fix yet)');
    }
    if (watchError) lines.push(`error: [${geoErrorName(watchError.code)}] ${watchError.message}`);
    lines.push('');
    lines.push('-- getCurrentPosition (one-shot) --');
    if (oneShotFix) {
      lines.push(`lat=${oneShotFix.latitude} lon=${oneShotFix.longitude}`);
      lines.push(`accuracy=${oneShotFix.accuracy}m`);
      lines.push(`fix_ts=${new Date(oneShotFix.timestamp).toISOString()}`);
      lines.push(`fix_age=${formatAge(now - oneShotFix.timestamp)}`);
    } else {
      lines.push('(not requested)');
    }
    if (oneShotError) lines.push(`error: [${geoErrorName(oneShotError.code)}] ${oneShotError.message}`);
    return lines.join('\n');
  };

  const handleSendToSupport = async () => {
    setSubmitPhase('sending');
    setSubmitError(null);
    try {
      const report = await collectDiagnostics({
        context: {
          type: 'gps',
          gps: {
            watch_fix: watchFix,
            watch_count: watchCount,
            watch_error: watchError,
            one_shot_fix: oneShotFix,
            one_shot_error: oneShotError,
            accuracy_min: accuracyMin,
            accuracy_max: accuracyMax,
            permission,
            rep_note: repNote || undefined,
          },
          attachments: {
            notes: attachments.notes.trim(),
            screenshots: attachments.screenshots,
          },
        },
      });
      const result = await diagnosticsApi.submit(report);
      setSubmitRefId(result.report_id);
      setSubmitPhase('sent');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
      setSubmitPhase('error');
    }
  };

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(buildClipboardText());
    } catch {
      // Fallback - prompt user with the text in a window prompt
      window.prompt('Copy GPS diagnostics:', buildClipboardText());
    }
  };

  const center = useMemo<[number, number] | null>(() => {
    if (watchFix) return [watchFix.latitude, watchFix.longitude];
    if (oneShotFix) return [oneShotFix.latitude, oneShotFix.longitude];
    return null;
  }, [watchFix, oneShotFix]);

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-100"
            aria-label="Back"
          >
            <svg className="h-6 w-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-slate-800">GPS Diagnostics</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Permission state pill */}
        <div className="rounded-xl bg-white p-3 shadow-card flex items-center justify-between">
          <span className="text-sm text-slate-600">Location permission</span>
          <span
            className={
              'rounded-full px-3 py-1 text-xs font-medium ' +
              (permission === 'granted'
                ? 'bg-green-100 text-green-800'
                : permission === 'denied'
                ? 'bg-red-100 text-red-800'
                : 'bg-slate-200 text-slate-700')
            }
          >
            {permission}
          </span>
        </div>

        {/* #2208 — the "granted but OS denies" case (diag #49/#50/#53): the site
            permission is on, but the OS still returns PERMISSION_DENIED because
            Location Services is off for the browser. Show device-aware steps
            instead of the misleading raw "User denied Geolocation". */}
        {(isOsLocationServicesOff(watchError?.code ?? -1, { permissionGranted: permission === 'granted' }) ||
          isOsLocationServicesOff(oneShotError?.code ?? -1, { permissionGranted: permission === 'granted' })) && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="mb-1 font-semibold">⚠ Location Services look turned off for your browser</p>
            <p>
              {buildGeoErrorMessage(1, { permissionGranted: true, platform: detectGeoPlatform() })}
            </p>
            <p className="mt-2 text-xs text-amber-700">
              Your in-app permission shows “granted,” so this is a device setting, not the app.
            </p>
          </div>
        )}

        {/* Live map */}
        <div className="rounded-xl bg-white shadow-card overflow-hidden">
          <div className="px-4 pt-3 pb-2">
            <h2 className="text-sm font-medium text-slate-700">Live position (watchPosition)</h2>
            <p className="text-xs text-slate-500">
              High accuracy, maximumAge=0. Accuracy circle drawn around the reported point.
            </p>
          </div>
          <div style={{ height: 260, width: '100%' }}>
            {center ? (
              <MapContainer
                center={center}
                zoom={17}
                style={{ height: '100%', width: '100%' }}
                attributionControl
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={center} />
                {watchFix && (
                  <Circle
                    center={[watchFix.latitude, watchFix.longitude]}
                    radius={watchFix.accuracy}
                    pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.15 }}
                  />
                )}
                <MapRecenter lat={center[0]} lon={center[1]} />
              </MapContainer>
            ) : (
              <div className="flex h-full items-center justify-center bg-slate-100 text-sm text-slate-500">
                Waiting for first fix...
              </div>
            )}
          </div>
        </div>

        {/* watchPosition stats */}
        <div className="rounded-xl bg-white p-4 shadow-card">
          <h2 className="text-sm font-medium text-slate-700 mb-2">watchPosition stats</h2>
          {watchFix ? (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-slate-700">
              <dt className="text-slate-500">fixes received</dt>
              <dd>{watchCount}</dd>
              <dt className="text-slate-500">lat / lon</dt>
              <dd>
                {watchFix.latitude.toFixed(6)}, {watchFix.longitude.toFixed(6)}
              </dd>
              <dt className="text-slate-500">accuracy</dt>
              <dd>
                <span
                  className={
                    watchFix.accuracy > 100
                      ? 'text-red-700 font-semibold'
                      : watchFix.accuracy > 30
                      ? 'text-amber-700'
                      : 'text-green-700 font-semibold'
                  }
                >
                  {watchFix.accuracy.toFixed(1)} m
                </span>
              </dd>
              <dt className="text-slate-500">accuracy range</dt>
              <dd>
                {accuracyMin?.toFixed(1) ?? '?'} – {accuracyMax?.toFixed(1) ?? '?'} m
              </dd>
              <dt className="text-slate-500">altitude</dt>
              <dd>
                {watchFix.altitude === null
                  ? '—'
                  : `${watchFix.altitude.toFixed(1)} m (±${(watchFix.altitudeAccuracy ?? 0).toFixed(0)})`}
              </dd>
              <dt className="text-slate-500">heading</dt>
              <dd>{watchFix.heading === null ? '—' : `${watchFix.heading.toFixed(0)}°`}</dd>
              <dt className="text-slate-500">speed</dt>
              <dd>{watchFix.speed === null ? '—' : `${watchFix.speed.toFixed(1)} m/s`}</dd>
              <dt className="text-slate-500">fix timestamp</dt>
              <dd>{new Date(watchFix.timestamp).toLocaleTimeString()}</dd>
              <dt className="text-slate-500">fix age</dt>
              <dd>{formatAge(now - watchFix.timestamp)}</dd>
            </dl>
          ) : (
            <p className="text-sm text-slate-500">No fix yet…</p>
          )}
          {watchError && (
            <div className="mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-700">
              <div className="font-semibold">{geoErrorName(watchError.code)}</div>
              <div>{watchError.message}</div>
            </div>
          )}
        </div>

        {/* One-shot getCurrentPosition */}
        <div className="rounded-xl bg-white p-4 shadow-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-slate-700">getCurrentPosition (one-shot)</h2>
            <button
              onClick={runOneShot}
              disabled={oneShotRunning}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:bg-slate-300"
            >
              {oneShotRunning ? 'Requesting…' : 'Request fix'}
            </button>
          </div>
          {oneShotFix ? (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-slate-700">
              <dt className="text-slate-500">lat / lon</dt>
              <dd>
                {oneShotFix.latitude.toFixed(6)}, {oneShotFix.longitude.toFixed(6)}
              </dd>
              <dt className="text-slate-500">accuracy</dt>
              <dd>{oneShotFix.accuracy.toFixed(1)} m</dd>
              <dt className="text-slate-500">fix age</dt>
              <dd>{formatAge(now - oneShotFix.timestamp)}</dd>
            </dl>
          ) : (
            <p className="text-sm text-slate-500">No one-shot fix requested yet.</p>
          )}
          {oneShotError && (
            <div className="mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-700">
              <div className="font-semibold">{geoErrorName(oneShotError.code)}</div>
              <div>{oneShotError.message}</div>
            </div>
          )}
        </div>

        {/* Copy button + UA */}
        <div className="rounded-xl bg-white p-4 shadow-card space-y-3">
          <button
            onClick={copyDiagnostics}
            className="w-full rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Copy diagnostics to clipboard
          </button>
          <p className="text-xs text-slate-500 font-mono break-all">{navigator.userAgent}</p>
        </div>

        {/* Send to support (#1873) */}
        {submitPhase === 'sent' ? (
          <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-center">
            <svg className="mx-auto h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 font-semibold text-green-800">Sent to support</p>
            {submitRefId !== null && <p className="mt-1 text-xs font-mono text-green-700">Reference: #{submitRefId}</p>}
          </div>
        ) : (
          <div className="rounded-xl bg-white p-4 shadow-card space-y-3">
            <div>
              <h2 className="text-sm font-medium text-slate-700">Send GPS report to support</h2>
              <p className="mt-1 text-xs text-slate-500">
                Includes the current and recent GPS fixes, device info, network, and bandwidth test results.
              </p>
            </div>
            <textarea
              value={repNote}
              onChange={(e) => setRepNote(e.target.value)}
              placeholder="(Optional GPS-specific note) e.g. 'app says I'm far from store but I'm in the parking lot'"
              rows={2}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
            />
            <DiagnosticAttachments
              value={attachments}
              onChange={setAttachments}
              disabled={submitPhase === 'sending'}
              notesLabel="More details & screenshots"
              notesHint="Anything else support should know? Add iPhone screenshots of what you're seeing."
            />
            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{submitError}</div>
            )}
            <button
              onClick={handleSendToSupport}
              disabled={submitPhase === 'sending'}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 py-3 font-semibold text-white shadow-lg disabled:opacity-50"
            >
              {submitPhase === 'sending' ? (
                <>
                  <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending…
                </>
              ) : (
                'Send to Support'
              )}
            </button>
          </div>
        )}

        {/* Reading guide */}
        <div className="rounded-xl bg-blue-50 p-4 text-xs text-blue-900 space-y-1">
          <p className="font-medium">How to read this</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>
              <span className="font-mono">accuracy &lt; 30 m</span> = real GPS lock (good)
            </li>
            <li>
              <span className="font-mono">30 – 100 m</span> = GPS but degraded (urban canyon, near building)
            </li>
            <li>
              <span className="font-mono">&gt; 100 m</span> = likely WiFi/cell triangulation, not actual GPS. This is what causes "far from store" false reports.
            </li>
          </ul>
          <p>
            Stand still outdoors for 15–30 seconds — accuracy should drop below 30 m. If it doesn't, the device isn't getting a real GPS lock and check-ins from this device will be unreliable.
          </p>
        </div>
      </div>
    </div>
  );
}
